import {
    BufferGeometry,
    Color,
    LineSegments,
    MathUtils,
    ShaderMaterial,
    Triangle,
    Vector3,
} from 'three';
import { Float32BufferAttribute } from 'three/src/core/BufferAttribute';


const vertexShader = `
	attribute vec3 control0;
	attribute vec3 control1;
	attribute vec3 direction;
    attribute float collapse;

	void main() {
	
		// Transform the line segment ends and control points into camera clip space
		vec4 c0 = projectionMatrix * modelViewMatrix * vec4( control0, 1.0 );
		vec4 c1 = projectionMatrix * modelViewMatrix * vec4( control1, 1.0 );
		vec4 p0 = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
		vec4 p1 = projectionMatrix * modelViewMatrix * vec4( position + direction, 1.0 );

		c0.xy /= c0.w;
		c1.xy /= c1.w;
		p0.xy /= p0.w;
		p1.xy /= p1.w;

		// Get the direction of the segment and an orthogonal vector
		vec2 dir = p1.xy - p0.xy;
		vec2 norm = vec2( -dir.y, dir.x );

		// Get control point directions from the line
		vec2 c0dir = c0.xy - p1.xy;
		vec2 c1dir = c1.xy - p1.xy;

		// If the vectors to the controls points are pointed in different directions away
		// from the line segment then the line should not be drawn.
		float d0 = dot( normalize( norm ), normalize( c0dir ) );
		float d1 = dot( normalize( norm ), normalize( c1dir ) );
		float discardFlag = float( sign( d0 ) != sign( d1 ) );
    
        vec3 p = position + ((discardFlag > 0.5) ? direction * collapse : vec3(0.));    
        vec4 mvPosition = modelViewMatrix * vec4( p, 1.0 );
		gl_Position = projectionMatrix * mvPosition;
	}
`;

const fragmentShader = `
    uniform vec3 diffuse;
	uniform float opacity;

	void main() {
	
		vec3 outgoingLight = vec3( 0.0 );
		vec4 diffuseColor = vec4( diffuse, opacity );
		
		outgoingLight = diffuseColor.rgb; // simple shader
	
		gl_FragColor = vec4( outgoingLight, diffuseColor.a );
	}
`;


class OutlineEdges extends BufferGeometry{

    constructor ( mesh, params = {}) {

        super();

        const _v0 = new Vector3();
        const _v1 = new Vector3();
        const _normal = new Vector3();
        const _triangle = new Triangle();

        const geometry = mesh.geometry;

        this.type = 'EdgesGeometry';

        this.parameters = {
            thresholdAngle: params.hasOwnProperty('thresholdAngle') ? params.thresholdAngle : 1,
            transparent: params.hasOwnProperty('transparent') ? params.transparent : false,
            opacity: params.hasOwnProperty('opacity') ? params.opacity : 1.0,
            color: params.hasOwnProperty('color') ? params.color : '0xffffff'
        };

        const precisionPoints = 4;
        const precision = Math.pow( 10, precisionPoints );
        const thresholdDot = Math.cos( MathUtils.DEG2RAD * this.parameters.thresholdAngle );

        const indexAttr = geometry.getIndex();
        const positionAttr = geometry.getAttribute( 'position' );
        const indexCount = indexAttr ? indexAttr.count : positionAttr.count;

        const indexArr = [ 0, 0, 0 ];
        const vertKeys = [ 'a', 'b', 'c' ];
        const hashes = new Array( 3 );

        const edgeData = {};
        const vertices = [];
        const control0 = [];
        const control1 = [];
        const direction = [];
        const collapse = [];

        // generate vertices
        const v3 = new Vector3();
        const n = new Vector3();
        const n1 = new Vector3();
        const n2 = new Vector3();
        const d = new Vector3();

        for ( let i = 0; i < indexCount; i += 3 ) {

            if ( indexAttr ) {

                indexArr[ 0 ] = indexAttr.getX( i );
                indexArr[ 1 ] = indexAttr.getX( i + 1 );
                indexArr[ 2 ] = indexAttr.getX( i + 2 );

            } else {

                indexArr[ 0 ] = i;
                indexArr[ 1 ] = i + 1;
                indexArr[ 2 ] = i + 2;

            }

            const { a, b, c } = _triangle;
            a.fromBufferAttribute( positionAttr, indexArr[ 0 ] );
            b.fromBufferAttribute( positionAttr, indexArr[ 1 ] );
            c.fromBufferAttribute( positionAttr, indexArr[ 2 ] );
            _triangle.getNormal( _normal );

            // create hashes for the edge from the vertices
            hashes[ 0 ] = `${ Math.round( a.x * precision ) },${ Math.round( a.y * precision ) },${ Math.round( a.z * precision ) }`;
            hashes[ 1 ] = `${ Math.round( b.x * precision ) },${ Math.round( b.y * precision ) },${ Math.round( b.z * precision ) }`;
            hashes[ 2 ] = `${ Math.round( c.x * precision ) },${ Math.round( c.y * precision ) },${ Math.round( c.z * precision ) }`;

            // skip degenerate triangles
            if ( hashes[ 0 ] === hashes[ 1 ] || hashes[ 1 ] === hashes[ 2 ] || hashes[ 2 ] === hashes[ 0 ] ) {

                continue;

            }

            // iterate over every edge
            for ( let j = 0; j < 3; j ++ ) {

                // get the first and next vertex making up the edge
                const jNext = ( j + 1 ) % 3;
                const vecHash0 = hashes[ j ];
                const vecHash1 = hashes[ jNext ];
                const v0 = _triangle[ vertKeys[ j ] ];
                const v1 = _triangle[ vertKeys[ jNext ] ];

                const hash = `${ vecHash0 }_${ vecHash1 }`;
                const reverseHash = `${ vecHash1 }_${ vecHash0 }`;

                if ( reverseHash in edgeData && edgeData[ reverseHash ] ) {

                    // if we found a sibling edge add it into the vertex array if
                    // it meets the angle threshold and delete the edge from the map.
                    if ( _normal.dot( edgeData[ reverseHash ].normal ) <= thresholdDot ) {

                        vertices.push( v0.x, v0.y, v0.z );
                        vertices.push( v1.x, v1.y, v1.z );

                        d.subVectors(v1, v0);
                        collapse.push(0, 1);
                        n.copy(d).normalize();
                        direction.push(d.x, d.y, d.z);
                        n1.copy(_normal);
                        n1.crossVectors(n, n1);
                        d.subVectors(v0, v1);
                        n.copy(d).normalize();
                        n2.copy(edgeData[ reverseHash ].normal);
                        n2.crossVectors(n, n2);
                        direction.push(d.x, d.y, d.z);

                        v3.copy(v0).add(n1); // control0
                        control0.push(v3.x, v3.y, v3.z);
                        v3.copy(v0).add(n2); // control1
                        control1.push(v3.x, v3.y, v3.z);

                        v3.copy(v1).add(n1); // control0
                        control0.push(v3.x, v3.y, v3.z);
                        v3.copy(v1).add(n2); // control1
                        control1.push(v3.x, v3.y, v3.z);

                    }

                    edgeData[ reverseHash ] = null;

                } else if ( ! ( hash in edgeData ) ) {

                    // if we've already got an edge here then skip adding a new one
                    edgeData[ hash ] = {

                        index0: indexArr[ j ],
                        index1: indexArr[ jNext ],
                        normal: _normal.clone(),

                    };

                }

            }

        }

        // iterate over all remaining, unmatched edges and add them to the vertex array
        for ( const key in edgeData ) {

            if ( edgeData[ key ] ) {
                const { index0, index1 } = edgeData[ key ];

                _v0.fromBufferAttribute( positionAttr, index0 );
                _v1.fromBufferAttribute( positionAttr, index1 );

                vertices.push( _v0.x, _v0.y, _v0.z );
                vertices.push( _v1.x, _v1.y, _v1.z );

                collapse.push(0, 1);
                direction.push(0, 0, 0);
                direction.push(0, 0, 0);
                control0.push(0, 0, 0);
                control1.push(0, 0, 0);
                control0.push(0, 0, 0);
                control1.push(0, 0, 0);
            }

        }

        this.setAttribute( 'position', new Float32BufferAttribute( vertices, 3 ) );
        this.setAttribute('control0', new Float32BufferAttribute(control0, 3));
        this.setAttribute('control1', new Float32BufferAttribute(control1, 3));
        this.setAttribute('direction', new Float32BufferAttribute(direction, 3));
        this.setAttribute('collapse', new Float32BufferAttribute(collapse, 1));

        const shaderMaterial = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                diffuse: {
                    value: new Color().setHex(this.parameters.color),
                },
                opacity: {
                    value: this.parameters.opacity,
                }
            },
            transparent: this.parameters.transparent,
        });

        const outline = new LineSegments(this,shaderMaterial);
        outline.name = 'Outline';

        mesh.add(outline);
    }

}

export { OutlineEdges };